import { connect } from 'dva';
import { namespace } from '../model';
import { TDispatchFun, SortableItems } from '@/pages/datalab/components/SortableDrag/configration';
import { SortableAfter, SortableType } from '@dbl/core/chartEditor';
import { ChartType } from '@dbl/core/chart';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import React, { useCallback, useEffect, useState } from 'react';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { ISortableOption } from '@/pages/datalab/components/SortableDrag/typeDefine';
import { DBColumnType } from '@/pages/datalab/components/SortableDrag/typeDefine';
import { useForm, useField } from 'react-final-form-hooks';
import { Card, Button, Form, Select, Radio, Input, notification, Bubble, ConfigProvider } from 'tea-component';
import { FilterSortable } from '@/pages/datalab/components/SortableDrag/Sortable/filterSortable';
import '../../Analysis/components/Search/index.scss';
import { history } from '@@/core/history';
import {checkNullString, useDblIntl} from '@/pages/datalab/utils/utils';
import { dragOnMove } from '@/pages/datalab/components/SortableDrag/utils';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { SYSTEM_NAME } from '@dbl/core/global';
import { CustomPrompt } from '@/pages/datalab/components/CustomPrompt';
import {useSelector} from "umi";

function getStatus(meta, validating) {
  if (meta.active && validating) {
    return 'validating';
  }
  if (!meta.touched) {
    return undefined;
  }
  return meta.error ? 'error' : 'success';
}

export default connect((state: any) => {
  const {
    loading: { effects },
  } = state;
  const {
    dataTables,
    columnFilters = [],
    sensitiveColumns = [],
    dragState,
    saveObj,
    hashEqualForSave,
    dataTableJoinRelations,
    mode,
  } = state[namespace];

  return {
    dataTables,
    columnFilters,
    sensitiveColumns,
    dragState: { ...dragState },
    saveObj,
    hashEqualForSave,
    dataTableJoinRelations,
    mode,
    loading: effects[`${namespace}/getTableList`],
    submitLoading: effects[`${namespace}/saveCondition`],
  };
})(PackageEdit);

function PackageEdit(props: any) {
  const {dblFormatMessage: extractionFormatMessage, commonFormatMessage, isEn} = useDblIntl("extraction");
  const { gameCode } = useSelector((state) => ({gameCode: state?.datalab?.gameCode}));
  const {
    dataTables,
    columnFilters,
    sensitiveColumns,
    dragState,
    saveObj,
    hashEqualForSave,
    dataTableJoinRelations,
    mode,
    loading,
    submitLoading,
  } = props;

  // 人群包页暂无自定义字段弹窗，占位以满足 FilterSortable 契约
  const showCustomColumnModel = useCallback((_item: IColumnInfo) => {}, []);
  const dispatchFun: TDispatchFun = (path: string, payload: {}, callback?: Function) => {
    props.dispatch({
      type: `${namespace}/${path}`,
      payload,
      callback,
    });
  };

  const dragOnMoveHandle = useCallback(
    (evt: any, originalEvent: any, dispatchFun: TDispatchFun, currentDragItem: IColumnInfo | null) => {
      const { to } = evt;
      const sortableClass = to.className.split(' ').find((item: string) => item.includes(`-${SortableItems}`));
      if (!sortableClass) {
        return false;
      }
      const sortableKeyMove = sortableClass.slice(0, sortableClass.indexOf(`-${SortableItems}`));
      const { [sortableKeyMove]: columnInfos } = { columnFilters };
      return dragOnMove(evt, originalEvent, sortableKeyMove, columnInfos, dispatchFun, currentDragItem);
    },
    [columnFilters],
  );
  
  const [hover, setHover] = useState(false);
  const [sortableOption, setSortableOption] = useState<ISortableOption>({
    canDrag: true,
    dragState,
    dragMoveHandle: dragOnMoveHandle,
    dispatch: props.dispatch,
  });
  
  useEffect(() => {
    setSortableOption(prev => ({
      ...prev,
      dragState,
      dragMoveHandle: dragOnMoveHandle,
    }));
  }, [dragState, dragOnMoveHandle]);

  const sensitiveOptions = sensitiveColumns.map((item: IColumnInfo) => ({
    text: item.column_display || item.column_name, // 名称展示逻辑: 旧数据中的中文名称或原始名称
    value: `${item.table_id}.${item.column_name}`,
  }));

  const appendFilters: IColumnInfo[] = [
    ...columnFilters.map((item: IColumnInfo) => ({
      ...item,
      column_text: DBColumnType.getFilterText(item, isEn),
    })),
    {
      id: '',
      column_type: SortableAfter.Placeholder,
    },
    {
      id: '',
      column_type: SortableAfter.ErrorMessage,
    },
  ];

  const onSubmit = (formData: any) => {
    const params = {
      ...formData,
      game_code: gameCode,
    };
    dispatchFun('saveCondition', { params }, (param: any, resp: any) => {
      // 保存成功后跳转
      if (resp.code === 0) {
        notification.success({
          description: extractionFormatMessage("save_success", {name: param?.name}),
        });
        history.push(`/${SYSTEM_NAME}/${gameCode}/DataManagement/UserPackagesManagement`);
      }
    });
  };

  const { form, handleSubmit, validating, dirty, submitSucceeded } = useForm({
    onSubmit,
    initialValuesEqual: () => true,
    validate: ({ name, type, sensitiveValue }: { name: string; type: number; sensitiveValue: string; }) => ({
      name: !name ? extractionFormatMessage("package_name_placeholder") : undefined,
      type: !type ? extractionFormatMessage("package_note_placeholder") : undefined,
      sensitiveValue: !sensitiveValue ? extractionFormatMessage('extract_field_placeholder') : undefined,
    }),
  });

  const name = useField('name', form);
  const type = useField('type', form);
  const sensitiveValue = useField('sensitiveValue', form);
  const remark = useField('remark', form);

  useEffect(() => {
    const sensitiveValue = saveObj?.column_infos?.table_id
      ? `${saveObj.column_infos.table_id}.${saveObj.column_infos.column_name}`
      : '';
    const params = { ...saveObj, sensitiveValue };
    form.initialize(params);
  }, [JSON.stringify(saveObj)]);

  if (loading) {
    // loading 页面
    return <>loading</>;
  }
  
  return (
    <ConfigProvider locale={isEn ? "en" : "zh"}>
      <Card className="add-cread dtb-dl-ans-search-container">
        <Card.Body>
          <div className="add_task-title">{`${checkNullString(saveObj.package_uuid) ? extractionFormatMessage("add_package") : saveObj.name}`}</div>
          <Form>
            <form onSubmit={handleSubmit}>
              <Form.Item
                label={
                  <div className="text-label form-title">
                    {extractionFormatMessage("filter_conditions")}
                  </div>
                }
              >
                <div className="flex-item them-blue">
                  <FilterSortable
                    key="filter"
                    sortableKey={SortableType.Filter}
                    sortableList={appendFilters}
                    sortableOption={sortableOption}
                    setSortableOption={setSortableOption}
                    dispatchFun={dispatchFun}
                    dataTables={dataTables}
                    dataTableJoinRelations={dataTableJoinRelations || []}
                    chartType={ChartType.Table}
                    mode={mode || ChartCalculateMode.Aggregate}
                    dimensions={[]}
                    showCustomColumnModel={showCustomColumnModel}
                    system="datalab"
                    customColumns={[]}
                    dragPlaceholderSuffix=""
                  />
                </div>
              </Form.Item>
              <Form.Item
                label={
                  <div className="text-label">
                    {extractionFormatMessage("extract_field")} <span className="text-label-red">*</span>
                  </div>
                }
                status={getStatus(sensitiveValue.meta, validating)}
                message={getStatus(sensitiveValue.meta, validating) === 'error' && sensitiveValue.meta.error}
              >
                <div className="text-item-flex">
                  <Select
                    className="input-item"
                    matchButtonWidth
                    appearance="button"
                    placeholder={extractionFormatMessage("extract_field_placeholder")}
                    {...sensitiveValue.input}
                    searchable={sensitiveOptions.length > 0}
                    size="l"
                    options={sensitiveOptions}
                  />
                  <Bubble content={extractionFormatMessage("extract_field_info")} dark placement="top">
                    <i
                      onMouseMove={() => {
                        setHover(true);
                      }}
                      onMouseLeave={() => {
                        setHover(false);
                      }}
                      className="tip-icon" 
                    >
                     
                      <SvgIcon type={!hover ? 'alert-circle-m' : 'alert-circle-action'}/>
                      
                    </i>
                  </Bubble>
               
                </div>
              </Form.Item>
              <Form.Item
                label={
                  <div className="text-label">
                    {extractionFormatMessage("package_type")} <span className="text-label-red">*</span>
                  </div>
                }
                status={getStatus(type.meta, validating)}
                message={getStatus(type.meta, validating) === 'error' && type.meta.error}
              >
                <Radio.Group {...type.input}>
                  <Radio name="0">{extractionFormatMessage("static_package")}</Radio>
                  <Radio name="1">{extractionFormatMessage("updated_daily")}</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={
                  <div className="text-label">
                    {extractionFormatMessage("package_name")} <span className="text-label-red">*</span>
                  </div>
                }
                status={getStatus(name.meta, validating)}
                message={getStatus(name.meta, validating) === 'error' && name.meta.error}
              >
                <Input
                  maxLength="80"
                  {...name.input}
                  placeholder={extractionFormatMessage("package_name_placeholder")}
                  className="input-item"
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item
                label={<div className="text-label">{extractionFormatMessage("package_note")}</div>}
                status={getStatus(remark.meta, validating)}
                message={getStatus(remark.meta, validating) === 'error' && remark.meta.error}
              >
                <Input
                  {...remark.input}
                  placeholder={extractionFormatMessage("package_note_placeholder")}
                  maxLength="80"
                  className="input-item"
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Action className="form-action">
                <Button
                  type="primary"
                  disabled={JSON.stringify(dataTables) === '[]' || submitLoading || validating}
                  htmlType="submit"
                  loading={submitLoading}
                >
                  {commonFormatMessage("save")}
                </Button>
              </Form.Action>
            </form>
          </Form>
        </Card.Body>

        <CustomPrompt
          title={`${commonFormatMessage("leave")  } ${  extractionFormatMessage("title")  }?`}
          message={commonFormatMessage("leave_message")}
          isPrompt={(!submitSucceeded) && (dirty || !hashEqualForSave)}
        />
      </Card>
    </ConfigProvider>
  );
}

